import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import PageGuide from "components/admin/PageGuide";
import PageCard from "components/admin/PageCard";
import AdminTable from "components/admin/AdminTable";
import { useFetch, useRunner } from "components/admin/hooks";
import useConfirmChanges from "components/admin/confirmChanges";
import api from "api/client";
import { useAuth } from "auth/AuthContext";
import { PERMISSIONS } from "auth/access";
import { formatDuration, formatNumber } from "utils/format";

/** Tự tải lại danh sách người đang treo auto. */
const REFRESH_MS = 15000;

const toForm = (data) => ({ speedPercent: data.speedPercent, minVip: data.minVip });

function vipText(minVip) {
  if (minVip < 0) return "không ai được đánh nhanh";
  if (minVip === 0) return "mọi người chơi";
  return `từ VIP ${minVip} trở lên`;
}

function FastCell({ row }) {
  if (row.battleSpeed) {
    const fast = row.battleSpeed > 100;
    return (
      <Chip
        size="small"
        color={fast ? "success" : "default"}
        label={fast ? `Đang đánh ${row.battleSpeed}%` : "Đang đánh 100%"}
      />
    );
  }
  if (row.fastAllowed) return <Chip size="small" color="success" variant="outlined" label="Được" />;
  let reason = "Không";
  if (!row.unity) reason = "Bản JAR";
  else if (!row.clientFast) reason = "Client chưa cập nhật";
  return (
    <Tooltip title="Không đủ điều kiện, hoặc tốc độ đang để 100% / VIP chưa đủ">
      <Chip size="small" variant="outlined" label={reason} />
    </Tooltip>
  );
}

FastCell.propTypes = {
  row: PropTypes.shape({
    battleSpeed: PropTypes.number,
    fastAllowed: PropTypes.bool,
    unity: PropTypes.bool,
    clientFast: PropTypes.bool,
  }).isRequired,
};

function AutoFarm() {
  const navigate = useNavigate();
  const auth = useAuth();
  const canEdit = auth.can(PERMISSIONS.AUTO_EDIT);
  const auto = useFetch(() => api.get("/api/admin/auto"), [], REFRESH_MS);
  const [run, busy] = useRunner();
  const ask = useConfirmChanges();
  const [form, setForm] = useState(null);

  const data = auto.data;
  // Chỉ nạp lại form khi cài đặt đã lưu đổi, kẻo lần tự tải lại xoá mất phần đang sửa
  useEffect(() => {
    if (data) setForm(toForm(data));
  }, [data?.speedPercent, data?.minVip]);

  const dirty = data && form && JSON.stringify(toForm(data)) !== JSON.stringify(form);
  const set = (patch) => setForm({ ...form, ...patch });
  const speed = Number(form?.speedPercent) || 100;
  const minVip = Number(form?.minVip);

  const save = async () => {
    const yes = await ask({
      title: "Lưu cài đặt đánh nhanh khi auto",
      before: toForm(data),
      after: form,
      fields: [
        ["speedPercent", "Tốc độ trận khi auto", { text: (v) => `${v}%` }],
        ["minVip", "VIP tối thiểu được đánh nhanh"],
      ],
      notes: [
        "Áp dụng cho trận auto mới; trận đang đánh giữ tốc độ cũ.",
        "Đánh nhanh hơn = cày kinh nghiệm / đồ nhanh hơn — nên hỏi người cân bằng game trước khi tăng.",
      ],
    });
    if (!yes) return;
    const { ok, result } = await run(
      () =>
        api.put("/api/admin/auto", {
          speedPercent: Number(form.speedPercent) || 100,
          minVip: Number.isNaN(minVip) ? 0 : minVip,
        }),
      "Đã lưu cài đặt auto, áp dụng cho trận mới"
    );
    if (ok) auto.setData(result);
  };

  const actions = (
    <MDBox display="flex" gap={1}>
      {canEdit && dirty && (
        <MDButton size="small" color="white" disabled={busy} onClick={() => setForm(toForm(data))}>
          <Icon>undo</Icon>&nbsp;Bỏ sửa
        </MDButton>
      )}
      {canEdit && (
        <MDButton size="small" color="white" disabled={!dirty || busy} onClick={save}>
          <Icon>save</Icon>&nbsp;Lưu…
        </MDButton>
      )}
      {canEdit && data && (
        <Tooltip
          title={`Về mặc định: ${data.defaults.speedPercent}% · VIP ${data.defaults.minVip}`}
        >
          <MDButton
            size="small"
            color="white"
            disabled={busy}
            onClick={() =>
              setForm({ speedPercent: data.defaults.speedPercent, minVip: data.defaults.minVip })
            }
          >
            <Icon>restart_alt</Icon>&nbsp;Mặc định
          </MDButton>
        </Tooltip>
      )}
    </MDBox>
  );

  const sessions = data?.sessions || [];
  const fastNow = sessions.filter((s) => s.battleSpeed > 100).length;
  const fastAllowed = sessions.filter((s) => s.fastAllowed).length;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <PageGuide page="auto" />
        {auto.error && <Alert severity="error">{auto.error}</Alert>}
        {data && form && (
          <MDBox mb={3}>
            <PageCard
              title="Đánh nhanh khi auto"
              subtitle="Quái thường và boss thần chết của map, khu thường, bản Unity"
              color="info"
              actions={actions}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <MDInput
                    fullWidth
                    type="number"
                    label="Tốc độ trận khi auto (%)"
                    value={form.speedPercent}
                    disabled={!canEdit}
                    inputProps={{ min: 100, max: 200, step: 10 }}
                    helperText={
                      speed > 100
                        ? `Nhanh gấp ${(speed / 100).toLocaleString("vi-VN")} lần · 100 = tắt`
                        : "Đang tắt đánh nhanh"
                    }
                    onChange={(e) => set({ speedPercent: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <MDInput
                    fullWidth
                    type="number"
                    label="VIP tối thiểu được đánh nhanh"
                    value={form.minVip}
                    disabled={!canEdit}
                    inputProps={{ min: -1, max: data.maxVip }}
                    helperText={`Hiện: ${vipText(
                      Number.isNaN(minVip) ? 0 : minVip
                    )} · 0 = mọi người, -1 = không ai`}
                    onChange={(e) => set({ minVip: e.target.value })}
                  />
                </Grid>
              </Grid>
              <MDBox display="flex" gap={1} flexWrap="wrap" mt={2}>
                <Chip label={`Online: ${formatNumber(data.online)}`} />
                <Chip label={`Đang treo auto: ${formatNumber(sessions.length)}`} />
                <Chip label={`Được đánh nhanh: ${formatNumber(fastAllowed)}`} />
                <Chip color="success" label={`Đang trong trận nhanh: ${formatNumber(fastNow)}`} />
              </MDBox>
            </PageCard>
          </MDBox>
        )}

        <PageCard
          title="Đang treo auto"
          subtitle={`Tự tải lại mỗi ${REFRESH_MS / 1000} giây · bấm một dòng để xem người chơi`}
          color="success"
          noPadding
          actions={
            <Tooltip title="Tải lại">
              <MDButton size="small" color="white" onClick={() => auto.reload()}>
                <Icon>refresh</Icon>
              </MDButton>
            </Tooltip>
          }
        >
          <AdminTable
            loading={auto.loading && !data}
            rows={sessions}
            rowKey="userId"
            emptyText="Không có ai đang treo auto"
            onRowClick={(row) => navigate(`/players/${row.userId}`)}
            columns={[
              {
                key: "name",
                label: "Người chơi",
                render: (r) => (
                  <MDBox>
                    {r.name}
                    <MDTypography variant="caption" color="text" display="block">
                      {r.vip > 0 ? `VIP ${r.vip}` : "Chưa VIP"}
                      {r.unity ? "" : " · bản game cũ (JAR)"}
                    </MDTypography>
                  </MDBox>
                ),
              },
              {
                key: "map",
                label: "Đang ở",
                render: (r) =>
                  r.mapId >= 0 ? `${r.mapName || `Map ${r.mapId}`} · khu ${r.zoneId}` : "-",
              },
              { key: "time", label: "Đã treo", render: (r) => formatDuration(r.startedAt) },
              {
                key: "kills",
                label: "Quái / boss",
                align: "right",
                render: (r) => `${formatNumber(r.kills)} / ${formatNumber(r.bossKills)}`,
              },
              { key: "exp", label: "EXP", align: "right", render: (r) => formatNumber(r.exp) },
              { key: "coin", label: "Ngọc", align: "right", render: (r) => formatNumber(r.coin) },
              {
                key: "drops",
                label: "Rơi đồ",
                align: "right",
                render: (r) => formatNumber(r.drops),
              },
              {
                key: "losses",
                label: "Thua",
                align: "right",
                render: (r) => formatNumber(r.losses),
              },
              { key: "fast", label: "Đánh nhanh", render: (r) => <FastCell row={r} /> },
            ]}
          />
        </PageCard>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default AutoFarm;
